import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSearch } from '../../contexts/SearchContext';
import { useAuthDialog } from '../../contexts/AuthDialogContext';
import { useCart } from '../../contexts/CartContext';
import { ShoppingCart, User, BadgeInfo, ShoppingBag, Shield, Heart, Phone, Truck, ShieldCheck, Gift, RotateCcw, Users } from 'lucide-react';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { isAuthenticated, user } = useAuth();
  const { openSearch } = useSearch();
  const { openAuthDialog } = useAuthDialog();
  const { totalCount } = useCart();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCatalogMenuOpen, setIsCatalogMenuOpen] = useState(false);

  return (
    <>
      {/* Верхняя панель */}
      <div className="bg-gray-800 text-white text-sm hidden md:block">
        <div className="mx-auto max-w-container px-4 py-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4 overflow-x-auto">
              <Link to="/delivery" className="hover:text-gray-300 transition-colors whitespace-nowrap">Доставка и оплата</Link>
              <div className="w-px h-4 bg-gray-600"></div>
              <Link to="/returns" className="hover:text-gray-300 transition-colors whitespace-nowrap">Правила возврата</Link>
              <div className="w-px h-4 bg-gray-600"></div>
              <Link to="/tyres" className="hover:text-gray-300 transition-colors whitespace-nowrap">Шины</Link>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>+7 (812) 714-81-48</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Основной хедер */}
      <header className="sticky top-0 z-[9999] h-16 border-b transition-colors border-foreground/10 backdrop-blur-md">
        <nav className="mx-auto flex size-full max-w-container flex-row items-center gap-4 px-4 min-w-0">
          <Link to="/" className="inline-flex items-center gap-3 font-semibold shrink-0">
            <span className="text-black text-xl font-bold">parallel</span>
          </Link>

          <div className="flex flex-1 flex-row items-center justify-end md:gap-2 min-w-0 overflow-x-clip pr-1">
          <button
            type="button"
            onClick={openSearch}
            className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors duration-100 hover:bg-accent hover:text-accent-foreground p-1.5 [&_svg]:size-5 md:hidden"
            aria-label="Open Search"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </button>

          <button
            type="button"
            onClick={openSearch}
            className="inline-flex w-full max-w-[240px] items-center gap-2 rounded-full border p-1.5 text-sm text-gray-500 hover:bg-gray-100 transition-colors hover:bg-accent hover:text-accent-foreground max-md:hidden"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search ms-1 size-4" aria-label="Open Search">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
            <span className="text-muted-foreground">Поиск</span>
            <div className="ms-auto inline-flex gap-0.5 text-xs">
              <kbd className="rounded-md border bg-background px-1.5">⌘</kbd>
              <kbd className="rounded-md border bg-background px-1.5">K</kbd>
            </div>
          </button>

          <div className="relative group z-[200]">
            <button
              onClick={() => setIsCatalogMenuOpen(!isCatalogMenuOpen)}
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 transition-all duration-300 hover:bg-accent hover:text-accent-foreground max-md:gap-2 max-md:px-3"
            >
              <ShoppingBag className="size-4" aria-label="Каталог" />
              <span className="text-muted-foreground max-md:hidden">Каталог</span>
            </button>
            <div className={`absolute top-full md:left-0 right-0 mt-2 transition-all duration-300 transform z-[200] ${
              isCatalogMenuOpen 
                ? 'opacity-100 visible translate-y-0' 
                : 'md:opacity-0 md:invisible md:group-hover:opacity-100 md:group-hover:visible md:group-hover:translate-y-0 md:translate-y-2 opacity-0 invisible translate-y-2'
            }`}>
              {isCatalogMenuOpen && (
                <div 
                  className="fixed inset-0 z-[199] md:hidden"
                  onClick={() => setIsCatalogMenuOpen(false)}
                ></div>
              )}
              <div className="bg-white rounded-lg shadow-lg border overflow-hidden min-w-[180px] relative z-[200] md:min-w-[180px] w-full md:w-auto">
                <Link
                  to="/catalog-widget"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  onClick={() => setIsCatalogMenuOpen(false)}
                >
                  Каталог запчастей
                </Link>
                <div className="border-t"></div>
                <Link
                  to="/tyres"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  onClick={() => setIsCatalogMenuOpen(false)}
                >
                  Шины
                </Link>
              </div>
            </div>
          </div>

          <button 
            disabled
            className="hidden md:inline-flex items-center justify-center rounded-full border p-1.5 text-sm text-gray-400 cursor-not-allowed opacity-50"
          >
            <Heart className="size-4" />
          </button>

          <button 
            onClick={() => isAuthenticated ? navigate('/account') : openAuthDialog('login')}
            className="hidden md:inline-flex items-center justify-center rounded-full border p-1.5 text-sm text-gray-500 hover:bg-gray-100 transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <User className="size-4" />
          </button>

          <button 
            onClick={() => navigate('/cart')}
            className="hidden md:inline-flex items-center justify-center rounded-full border p-1.5 text-sm text-gray-500 hover:bg-gray-100 transition-colors hover:bg-accent hover:text-accent-foreground relative"
          >
            <ShoppingCart className="size-4" />
            {totalCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalCount > 99 ? '99+' : totalCount}
              </span>
            )}
          </button>

          {/* Админ панель для десктопа */}
          {isAuthenticated && user?.role === 'Admin' && (
            <Link
              to="/admin/markups"
              className="hidden md:inline-flex font-light items-center justify-center text-blue-600 hover:text-blue-800 rounded-md text-sm transition-colors duration-100 hover:bg-blue-50 p-1.5"
            >
              <Shield className="size-4 mr-1" />
              <span>Администратор</span>
            </Link>
          )}

          {/* Мобильное меню */}
          <div className="md:hidden relative z-[200]">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors duration-100 hover:bg-accent hover:text-accent-foreground p-1.5 [&_svg]:size-5"
              aria-label="Toggle Menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-menu">
                <line x1="4" x2="20" y1="12" y2="12"></line>
                <line x1="4" x2="20" y1="6" y2="6"></line>
                <line x1="4" x2="20" y1="18" y2="18"></line>
              </svg>
            </button>

            {/* Мобильное меню выпадающее */}
            {isMobileMenuOpen && (
              <>
                <div 
                  className="fixed inset-0 z-[199]"
                  onClick={() => setIsMobileMenuOpen(false)}
                ></div>
                <div className="fixed right-4 top-16 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-[200]">
                <div className="py-1">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/account"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <User className="size-4" />
                        <span>Личный кабинет</span>
                      </Link>
                      {user?.role === 'Admin' && (
                        <Link
                          to="/admin/markups"
                          className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <Shield className="size-4" />
                          <span>Панель администратора</span>
                        </Link>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        openAuthDialog('login');
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full text-left"
                    >
                      <User className="size-4" />
                      <span>Войти</span>
                    </button>
                  )}
                  
                  <button
                    disabled
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-400 cursor-not-allowed w-full text-left"
                  >
                    <Heart className="size-4" />
                    <span>Избранное</span>
                  </button>
                  
                  <Link
                    to="/cart"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <ShoppingCart className="size-4" />
                    <span>Корзина</span>
                    {totalCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {totalCount > 99 ? '99+' : totalCount}
                      </span>
                    )}
                  </Link>
                  
                  <Link
                    to="/project"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <BadgeInfo className="size-4" />
                    <span>О проекте</span>
                  </Link>
                </div>
                </div>
              </>
            )}
          </div>
        </div>
      </nav>
    </header>
    </>
  );
};

export default Header;
